// frontend/src/components/ObjectiveItem.js
// --- MODIFIED FILE ---
import React, { useState, useRef, useEffect } from 'react';
import { Trash2, Sparkles, Check, X, Wand2, Loader2, Plus, CornerDownLeft } from 'lucide-react'; 
import { apiService } from '../services/api'; 
import { MuseRewriteText, MuseThinking } from './Muse/MuseRewrite';

const ObjectiveItem = ({
  objective,
  onDelete,
  onUpdate,
  onDragStart,
  onDragOver,
  onDrop,
  sprintOptions,
  ownerOptions,
  predictedTokens,
  onPredictedTokenClick,
  suggestedTitle,
  isSuggestionDismissed,
  onAcceptSuggestion,
  onDeclineSuggestion,
  onRequestSuggestion, 
  museRewrite,
  museThinking,
  onUndoRewrite,
  onRewriteSettled,
  style,
  onRefresh, 
  ...rest
}) => {
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [isSuggestionVisible, setIsSuggestionVisible] = useState(false);
  const [isRequestingSuggestion, setIsRequestingSuggestion] = useState(false);

  const [isAddingTask, setIsAddingTask] = useState(false);
  const [newTaskName, setNewTaskName] = useState("");
  const [isSavingTask, setIsSavingTask] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const suggestionTimeoutRef = useRef(null);
  const newTaskInputRef = useRef(null);
  const HOVER_DELAY = 300;

  const handleToggleTask = (task) => {
    apiService.updateTask(task.id, { is_completed: !task.is_completed })
      .then(() => {
        onRefresh(); 
      })
      .catch(err => console.error("Erreur mise à jour tâche:", err));
  };
  
  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTaskName.trim()) {
      setIsAddingTask(false);
      setNewTaskName("");
      return;
    }
    
    setIsSavingTask(true);
    try {
      await apiService.createTaskForObjective(objective.id, { name: newTaskName.trim() });
      onRefresh(); 
    } catch (err) {
      console.error("Erreur création tâche:", err);
    } finally {
      setIsAddingTask(false);
      setNewTaskName("");
      setIsSavingTask(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
      if(window.confirm("Supprimer cette tâche ?")) {
          try {
              await apiService.deleteTask(taskId);
              onRefresh();
          } catch(err) {
              console.error("Erreur suppression de la tâche: ", err);
          }
      }
  };

  const handleKeyDownNewTask = (e) => {
    if (e.key === 'Enter') {
      handleAddTask(e);
    } else if (e.key === 'Escape') {
      setIsAddingTask(false);
      setNewTaskName("");
    }
  }

 useEffect(() => {
    if (isAddingTask && newTaskInputRef.current) {
        newTaskInputRef.current.focus();
    }
  }, [isAddingTask]);

  const handleFieldClick = (field, currentValue) => {
    setEditingField(field);
    setEditingValue(currentValue !== null ? currentValue.toString() : "");
  };

  const handleFieldChange = (e) => {
    setEditingValue(e.target.value);
  };

  const handleFieldBlur = () => {
    if (onUpdate && editingField) {
      let newValue;
      if (editingField === "effort") {
        newValue = editingValue === '' ? 0 : Number(editingValue);
      } else if (editingField === "sprint_id" || editingField === "owner_id") {
        newValue = editingValue === "" ? null : Number(editingValue);
      } else {
        newValue = editingValue;
      }
      onUpdate({ [editingField]: newValue });
    }
    setEditingField(null);
    setEditingValue("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); handleFieldBlur(); }
    else if (e.key === "Escape") { e.preventDefault(); setEditingField(null); setEditingValue(""); }
  };

  const getSprintDescription = () => {
    if (sprintOptions && objective.sprint_id) {
      const sprint = sprintOptions.find(s => Number(s.id) === Number(objective.sprint_id));
      return sprint ? (sprint.description || sprint.name || `Sprint ${sprint.id}`) : "-- Aucun --";
    }
    return "-- Aucun --";
  };

  const getOwnerFirstName = (ownerId) => {
    if (ownerOptions && ownerId) {
      const owner = ownerOptions.find(o => Number(o.id) === Number(ownerId));
      return owner ? owner.name.split(" ")[0] : "-- Inconnu --";
    }
    return "-- Aucun --";
  };

  const handleMouseEnter = () => {
    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setShowDetails(true);
    }, HOVER_DELAY);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeoutRef.current);
    setShowDetails(false);
  };

  const handleSuggestionEnter = () => {
    clearTimeout(suggestionTimeoutRef.current);
    setIsSuggestionVisible(true);
  };

  const handleSuggestionLeave = () => {
    suggestionTimeoutRef.current = setTimeout(() => {
      setIsSuggestionVisible(false);
    }, 300); 
  };
  
  const handleRequestSuggestionLocal = async (e) => {
    e.stopPropagation();
    if (isRequestingSuggestion || !onRequestSuggestion) return;
    setIsRequestingSuggestion(true);
    try {
      await onRequestSuggestion(objective.id, objective.name);
    } catch (error) {
      console.error("Erreur de suggestion:", error);
    } finally {
      setIsRequestingSuggestion(false);
    }
  };

  useEffect(() => {
    return () => {
      clearTimeout(hoverTimeoutRef.current);
      clearTimeout(suggestionTimeoutRef.current);
    };
  }, []);


  return (
    <div
      draggable={!editingField}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onClick={(e) => e.stopPropagation()}
      {...rest}
      style={style || {}}
      className="relative z-0 bg-white shadow rounded p-2 mb-1 text-sm w-[230px] max-w-[230px] min-h-[40px] flex flex-col cursor-move group/objectiveitem"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="font-medium flex items-center justify-between">
        {editingField === "name" ? (
          <input
            type="text" value={editingValue} onChange={handleFieldChange} onBlur={handleFieldBlur} onKeyDown={handleKeyDown}
            className="border rounded p-1 flex-grow mr-2" autoFocus
          />
        ) : (
          <span className="flex-grow mr-2 break-words" onClick={() => handleFieldClick("name", objective.name)}>
            {museRewrite ? (
              <MuseRewriteText key={museRewrite.to} from={museRewrite.from} to={museRewrite.to}
                onUndo={onUndoRewrite ? () => onUndoRewrite(objective.id) : undefined}
                onSettled={onRewriteSettled ? () => onRewriteSettled(objective.id) : undefined} />
            ) : museThinking ? <MuseThinking>{objective.name}</MuseThinking> : objective.name}
          </span>
        )}
        <div className="flex items-center space-x-1 flex-shrink-0">
          {(!suggestedTitle || isSuggestionDismissed) && (
            <button onClick={handleRequestSuggestionLocal} disabled={isRequestingSuggestion} className="p-1 text-purple-500 rounded-full hover:bg-purple-100 opacity-0 group-hover/objectiveitem:opacity-100 transition-opacity" title="Suggérer une reformulation">
              {isRequestingSuggestion ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            </button>
          )}

          {suggestedTitle && !isSuggestionDismissed && (
            <div className="relative" onMouseEnter={handleSuggestionEnter} onMouseLeave={handleSuggestionLeave}>
                <Sparkles className="h-4 w-4 text-green-500 cursor-pointer" />
                {isSuggestionVisible && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-white border border-gray-200 rounded-lg shadow-xl p-2 z-30" onMouseEnter={handleSuggestionEnter} onMouseLeave={handleSuggestionLeave}>
                        <p className="text-xs text-gray-500 mb-1">Suggestion IA :</p>
                        <p className="font-normal text-sm mb-2">{suggestedTitle}</p>
                        <div className="flex justify-end space-x-2">
                            <button onClick={(e) => { e.stopPropagation(); onDeclineSuggestion(objective.id); }} className="p-1 rounded-full text-red-500 hover:bg-red-100"><X size={14}/></button>
                            <button onClick={(e) => { e.stopPropagation(); onAcceptSuggestion(objective.id, suggestedTitle); }} className="p-1 rounded-full text-green-600 hover:bg-green-100"><Check size={14}/></button>
                        </div>
                    </div>
                )}
            </div>
          )}
          {predictedTokens != null && predictedTokens !== objective.effort && (
            <button type="button" className="bg-blue-200 border border-blue-400 text-blue-800 text-xs font-semibold px-1.5 py-0.5 rounded-md shadow cursor-pointer" onClick={(e) => { e.stopPropagation(); onPredictedTokenClick && onPredictedTokenClick(e); }}>
              🤖 {predictedTokens}
            </button>
          )}
        </div>
      </div>

      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showDetails ? 'max-h-[500px] mt-1 opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="mt-1 text-xs text-gray-500" onClick={() => handleFieldClick("effort", objective.effort)}>
          Effort:{" "}
          {editingField === "effort" ? ( <input type="number" value={editingValue} onChange={handleFieldChange} onBlur={handleFieldBlur} onKeyDown={handleKeyDown} className="border rounded p-1 w-16" autoFocus /> ) : ( objective.effort )}
        </div>
        <div className="mt-1 text-xs text-gray-500" onClick={() => handleFieldClick("sprint_id", objective.sprint_id ? objective.sprint_id.toString() : "")}>
          Sprint:{" "}
          {editingField === "sprint_id" ? ( <select value={editingValue} onChange={handleFieldChange} onBlur={handleFieldBlur} className="border rounded p-1 w-full" autoFocus> <option value="">-- Aucun --</option> {sprintOptions && sprintOptions.map((sprint) => ( <option key={sprint.id} value={sprint.id}> {sprint.description || sprint.name || `Sprint ${sprint.id}`} </option> ))} </select> ) : ( getSprintDescription() )}
        </div>
        <div className="mt-1 text-xs text-gray-500" onClick={() => handleFieldClick("owner_id", objective.owner_id ? objective.owner_id.toString() : "")}>
          Owner:{" "}
          {editingField === "owner_id" ? ( <select value={editingValue} onChange={handleFieldChange} onBlur={handleFieldBlur} className="border rounded p-1 w-full" autoFocus> <option value="">-- Aucun --</option> {ownerOptions && ownerOptions.map((owner) => ( <option key={owner.id} value={owner.id}> {getOwnerFirstName(owner.id)} </option> ))} </select> ) : ( getOwnerFirstName(objective.owner_id) )}
        </div>
        <div className="mt-1 text-xs text-gray-600" onClick={() => handleFieldClick("description", objective.description || "")}>
          {editingField === "description" ? ( <textarea value={editingValue} onChange={handleFieldChange} onBlur={handleFieldBlur} onKeyDown={handleKeyDown} className="border rounded p-1 w-full text-xs" autoFocus /> ) : ( <p className="whitespace-pre-wrap break-words">{objective.description || <span className="italic text-gray-400">Ajouter une description...</span>}</p> )}
        </div>
        
        <div className="mt-2 pt-2 border-t">        <h4 className="text-xs font-semibold text-gray-600 mt-2">Tâches</h4>
        <div className="space-y-1 mt-1">
          {(objective.tasks || []).map(task => (
            <div key={task.id} className="flex items-center text-sm group/task">
              <input
                type="checkbox"
                checked={task.is_completed}
                onChange={() => handleToggleTask(task)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className={`ml-2 flex-grow ${task.is_completed ? 'line-through text-gray-500' : ''}`}>
                {task.name}
              </span>
              <button onClick={() => handleDeleteTask(task.id)} className="ml-2 text-gray-400 hover:text-red-500 opacity-0 group-hover/task:opacity-100 transition-opacity">
                 <Trash2 size={12} />
              </button>
            </div>
          ))}

          {isAddingTask ? (
            <form onSubmit={handleAddTask} className="flex items-center mt-1">
              <input
                ref={newTaskInputRef}
                type="text"
                value={newTaskName}
                onChange={(e) => setNewTaskName(e.target.value)}
                onKeyDown={handleKeyDownNewTask}
                onBlur={() => { if (!newTaskName) setIsAddingTask(false); }}
                placeholder="Nouvelle tâche..."
                className="text-sm border-b focus:outline-none focus:border-blue-500 w-full"
                autoFocus
                disabled={isSavingTask}
              />
              <button type="submit" disabled={isSavingTask} className="p-1 text-green-500 hover:text-green-700">
                {isSavingTask ? <Loader2 size={16} className="animate-spin"/> : <CornerDownLeft size={16}/>}
              </button>
            </form>
          ) : (
            <button onClick={() => setIsAddingTask(true)} className="flex items-center text-xs text-blue-600 hover:text-blue-800 mt-1">
              <Plus size={14} className="mr-1"/> Ajouter une tâche
            </button>
          )}
          </div>
        </div>

        <div className="mt-2 flex justify-end">
          <button onClick={(e) => { e.stopPropagation(); if(onDelete) onDelete(objective.id); }} className="text-red-500 hover:text-red-700" title="Supprimer l'objectif">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ObjectiveItem;